/**
 * The RemoteTransform pass. A RemoteTransform3D or RemoteTransform2D copies its own transform onto
 * the node its `remote_path` names (Godot's `_update_remote()`) each time a transform flush reaches
 * it. The pass replays the flushes up to one fixed frame, the one the `ref:godot` harness captures,
 * so a static render shows each target where that capture has it. The relay moves other nodes and
 * draws nothing itself, so ADR-0008 (what a node draws) does not govern it. It is a live scene pass
 * (ADR-0060), as Godot resolves the path with `get_node` on the live tree
 * (`remote_transform_3d.cpp:35-36`), across an instance border too. Measured on Godot 4.6.3.
 */
import { parentPathOf, relativeTo } from '../../../utils/nodePath.js';
import { descendsFromClass } from '../../../godot/classdb/classBaseTypes.js';
import { resolveNodePathLiteral } from '../../../godot/string/nodePath.js';
import { liveGlobalTransform3D, nodesOnTheWayTo } from '../../../r3f/liveGlobalTransform.js';
import { uniquePathsOfLiteral } from '../../../r3f/nodePathUniqueNames.js';
import { sameData } from '../../../utils/sameData.js';
import { HARNESS_CAPTURE, replayTransformFlushes, transformFlushesThrough, } from '../../../godot/scene/transformFlushes.js';
import { perObject } from '../../../utils/perObject.js';
import { localFromGlobal3D, moveTarget } from './relayPush.js';
import { childrenAMoveCarries, nodesAMoveReaches, relayOf } from './relayTree.js';
const NO_POSES = { placed: new Map(), drawn: new Map() };
/**
 * Where the relays of `tree` leave its nodes. Godot pushes a relay each time a transform flush
 * reaches it (`remote_transform_3d.cpp:118-126`, `remote_transform_2d.cpp:127-136`), and a push
 * queues every relay its target carries, so the pass replays the flushes to the `ref:godot` harness
 * frame. A relay cycle takes the pose of that frame. `relayOf` says which targets Godot refuses.
 */
export function applyRemoteTransforms(tree) {
    const relays = relaysOf(tree);
    if (relays.length === 0)
        return NO_POSES;
    const placed = new Map();
    const queuedBy = entriesQueuedByAMoveOf(tree, relays);
    const entered = entriesOnTheListAtEnterTree(tree, relays, queuedBy);
    const localRelayAt = new Map(relays.filter((r) => !r.flags.useGlobal).map((r) => [r.link.path, r]));
    // Godot recurses without end through a cycle of local relays and overflows its stack. The replay
    // stops at the relay that is already pushing.
    const pushing = new Set();
    const push = (relay, queue) => {
        if (pushing.has(relay) || !moveTarget(relay, tree, placed))
            return;
        pushing.add(relay);
        for (const queued of queuedBy(relay))
            queue(queued);
        // A local relay pushes the moment its own local transform changes (`node_3d.cpp:398-400`,
        // `canvas_item.h:173-178`).
        const localTarget = localRelayAt.get(relay.target.path);
        if (localTarget)
            push(localTarget, queue);
        pushing.delete(relay);
    };
    const flushes = harnessFlushes(tree.ctx.physicsClock);
    replayTransformFlushes(entered, flushes, (entry, queue) => {
        if (entry.kind === 'relay')
            push(entry, queue);
        else
            entry.notified = liveGlobalTransform3D(tree, entry.link.path, placed);
    });
    const drawn = drawnTransformsOf(entered.filter((entry) => entry.kind === 'visual'), tree, placed);
    return { placed, drawn };
}
/** The flushes by the harness frame, once for each clock: a clock is one object per value pair. */
const harnessFlushes = perObject((clock) => transformFlushesThrough(HARNESS_CAPTURE, clock));
/** The live tree as the relay walk reads it. A `%Name` resolves through the tree's unique names. */
const liveRelayView = perObject((tree) => ({
    typeOf: (link) => link.step.node.type,
    rawPropertiesOf: (link) => link.step.node.rawProperties,
    childrenOf: (link) => tree.fold(undefined, (child) => child, link),
    isAtOrAbove: (upper, link) => relativeTo(upper.path, link.path) !== undefined,
    nodeAt: (from, literal) => {
        const path = resolveNodePathLiteral(from.path, literal, uniquePathsOfLiteral(literal, from.path, tree));
        return path === null ? null : tree.at(path);
    },
}));
/** Every relay of `tree` that can move its target, in tree order. */
function relaysOf(tree) {
    const view = liveRelayView(tree);
    const relays = [];
    tree.fold(undefined, (link, _, descend) => {
        const relay = relayOf(view, link);
        if (relay) {
            const { target, relayClass, flags } = relay;
            relays.push({
                kind: 'relay',
                link,
                target,
                space: relayClass.space,
                reached: relayClass.reached,
                flags,
            });
        }
        descend();
    });
    return relays;
}
/**
 * The entries that entering the tree puts on the transform-change list, in tree order: every
 * visual a relay can move, a RemoteTransform3D in global coordinates, which alone asks for the
 * notification (`remote_transform_3d.cpp:101-102`, `node_3d.cpp:94`), and every RemoteTransform2D,
 * as every CanvasItem goes on the list (`canvas_item.cpp:365-367`).
 */
function entriesOnTheListAtEnterTree(tree, relays, queuedBy) {
    const entryAt = new Map();
    for (const relay of relays) {
        if (relay.space === 'Node2D' || relay.flags.useGlobal)
            entryAt.set(relay.link.path, relay);
        for (const entry of queuedBy(relay))
            entryAt.set(entry.link.path, entry);
    }
    const entered = [];
    tree.fold(undefined, (link, _, descend) => {
        const entry = entryAt.get(link.path);
        if (entry)
            entered.push(entry);
        descend();
    });
    return entered;
}
/** The entries a push of each relay queues, cached by target, as {@link entriesQueuedBy} gives them. */
function entriesQueuedByAMoveOf(tree, relays) {
    // Only a relay in global coordinates asks for the notification.
    const listenerAt = new Map(relays.filter((r) => r.flags.useGlobal).map((r) => [r.link.path, r]));
    const queuedByTarget = new Map();
    return ({ target, reached }) => {
        let queued = queuedByTarget.get(target.path);
        if (!queued) {
            queued = entriesQueuedBy(target, reached, tree, listenerAt);
            queuedByTarget.set(target.path, queued);
        }
        return queued;
    };
}
/**
 * The entries a move of `target` queues, in the order Godot queues them ({@link nodesAMoveReaches}).
 * Each listener comes from `listenerAt`, and each visual it meets joins it.
 */
function entriesQueuedBy(target, reached, tree, listenerAt) {
    return nodesAMoveReaches(liveRelayView(tree), target, reached).flatMap((link) => listenerOrVisualAt(link, listenerAt) ?? []);
}
/** The listener at `link`, or the visual it is, added to `listenerAt` on the first ask. */
function listenerOrVisualAt(link, listenerAt) {
    const listener = listenerAt.get(link.path);
    if (listener || !descendsFromClass(link.step.node.type, 'VisualInstance3D'))
        return listener;
    const visual = { kind: 'visual', link };
    listenerAt.set(link.path, visual);
    return visual;
}
/**
 * The local transforms render needs so each of `visuals` stands where its last notification drew
 * it, and each Node3D child of a visual drawn off its node pose that is no visual stands at its own
 * pose: a visual carries its children in render, but in Godot only the visual's own instance lags.
 */
function drawnTransformsOf(visuals, tree, placed) {
    const view = liveRelayView(tree);
    const drawn = new Map();
    const drawnPoses = new Map(placed);
    const visualPaths = new Set(visuals.map(({ link }) => link.path));
    const nodePose = (link) => liveGlobalTransform3D(tree, link.path, placed);
    const standAt = (link, global) => {
        if (sameData(global, liveGlobalTransform3D(tree, link.path, drawnPoses)))
            return;
        const local = localFromGlobal3D(global, parentPathOf(link.path), nodesOnTheWayTo(tree, link.path, drawnPoses));
        drawn.set(link.path, { transform: local });
        drawnPoses.set(link.path, { ...drawnPoses.get(link.path), transform: local });
    };
    // Each pose is read after the nodes above it have moved, as a moved parent carries its children.
    for (const visual of visuals) {
        const ownPose = nodePose(visual.link);
        const drawnPose = visual.notified ?? ownPose;
        standAt(visual.link, drawnPose);
        // A lagging parent can carry a lagging visual to its drawn pose already, so its children
        // need their own pose even when the visual itself takes no drawn transform.
        if (sameData(drawnPose, ownPose))
            continue;
        for (const child of childrenAMoveCarries(view, visual.link, 'Node3D')) {
            if (!visualPaths.has(child.path))
                standAt(child, nodePose(child));
        }
    }
    return drawn;
}
//# sourceMappingURL=remoteTransforms.js.map